import { z } from 'zod';
import { OPEN_WORLD_TICKET_MAX_LENGTH, OPEN_WORLD_TICKET_MIN_LENGTH } from './entry';

/**
 * **The transport that carries a direct battle socket.**
 *
 * The client opens a WebSocket straight to the `battle-service` and this module
 * is the only thing it says before the game protocol takes over. Decided in
 * [ADR 0140] and specified in `docs/BATTLE_TRANSPORT_CONTRACT.md`.
 *
 * ## Why there is a second version number
 *
 * `OPEN_WORLD_ROOM_PROTOCOL_VERSION` describes what a room SAYS. This one
 * describes how a connection is ESTABLISHED. They move for different reasons -
 * a new projectile field bumps the first and leaves this one alone - and
 * folding them together would make every room change invalidate every
 * handshake.
 *
 * ## Why the ticket is not in the URL
 *
 * A URL reaches proxy access logs, browser history, the `Referer` header and
 * per-route metrics. The repository already knows a ticket escapes from exactly
 * there and defends itself with single use; single use is the second line, and
 * the first is not publishing it. A Durable Object had no choice, because it is
 * addressed FROM the ticket. A host of our own accepts the socket first and
 * decides after.
 *
 * The consequence runs through everything below: **authorisation can no longer
 * be a 401 before the socket exists.** When a ticket is refused the socket is
 * already open, so a refusal is a frame followed by a close - which is why
 * `accepted` and `terminal` exist here and have no counterpart in the room.
 *
 * ## The discriminant rule
 *
 * **Every transport frame's `t` starts with `t`; no game frame's does.** The
 * room protocol already spends `bc be bf bp bs bx c cg ci cmd cq e ff fr fs fx
 * ka kb kc kr la lb lc lg lo lr lt lx mf mo mr ms mv s w x xr xt` - `x` among
 * them, which is why the contract's first draft could not have its terminal
 * error be `x`. The prefix is not decoration: it makes a collision impossible
 * to introduce by accident, and `battle-transport.test.ts` proves it against
 * the room's own union rather than against a list somebody has to maintain.
 */

/**
 * The version of THIS handshake.
 *
 * A mismatch is refused rather than negotiated down. A host that spoke an older
 * handshake to be helpful would be guessing what the client meant by fields it
 * has never seen.
 */
export const BATTLE_TRANSPORT_VERSION = 1;

/* -------------------------------------------------------------------------- */
/*  Limits                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * **How many hulls a quadrant holds at once: thirty-two.**
 *
 * The number ADR 0140 decided and that nothing in the repository wrote down
 * until now - `OPEN_WORLD_MAX_PARTICIPANTS_PER_QUADRANT` is environment, may be
 * `null`, and exists so a bench matrix can vary without editing code. That is
 * right for a bench and wrong for a product: with no value the transport's
 * `full` refusal is a category with no trigger.
 *
 * **Thirty-two is technical seating; the product requirement is thirty
 * EFFECTIVE.** The two spare seats are the slack replacement uses so occupancy
 * never falls below the floor during a crossing or a reconnection - not a
 * promise of thirty-two simultaneous players.
 *
 * The environment variable still wins where it is set. This is the default a
 * deployment gets when nobody says otherwise, which is the case that was
 * silently broken before.
 */
export const OPEN_WORLD_QUADRANT_CAPACITY = 32;

/** The occupancy a quadrant must sustain for the product to be what it claims. */
export const OPEN_WORLD_QUADRANT_EFFECTIVE_FLOOR = 30;

/**
 * The largest first message the host will even keep in memory.
 *
 * **Derived, not chosen.** A ticket is already bounded at
 * {@link OPEN_WORLD_TICKET_MAX_LENGTH} by the route that mints it, and the
 * envelope around it is a fixed handful of bytes; the slack covers JSON
 * escaping and nothing else. Checked on the RAW message before `JSON.parse`,
 * for the reason `OPEN_WORLD_MAX_COMMAND_BYTES` already gives: parsing is the
 * expensive step, and a megabyte of nonsense must cost a length check.
 */
export const BATTLE_TRANSPORT_MAX_ATTACH_BYTES = OPEN_WORLD_TICKET_MAX_LENGTH + 256;

/**
 * How long a socket may stay open having said nothing.
 *
 * An honest client sends `attach` from its `onopen` handler, so this is orders
 * of magnitude above any real round trip. It is expressed against the ticket's
 * own lifetime so it can never drift past it: a socket that is still silent
 * when its ticket is halfway to expiry is not a slow client.
 */
export const BATTLE_TRANSPORT_AUTH_TIMEOUT_MS = 5_000;

/**
 * How often each side proves the socket is alive.
 *
 * The Durable Object never needed this: the runtime hands sockets back and says
 * when they close. A Node process behind a proxy is told nothing, so a
 * half-open socket - unplugged cable, expired NAT entry - is indistinguishable
 * from a quiet player and holds a chair for the whole three-minute
 * reconnection window. At 32 seats that is capacity lost to a failure nobody
 * can see.
 *
 * A heartbeat advances NOTHING. The room's clock is its own timer; since gate
 * V28I.BX.2 a message does not step it, and this is not an exception. It also
 * does not count against the 90 commands per second, because it never reaches
 * the room.
 */
export const BATTLE_TRANSPORT_HEARTBEAT_MS = 15_000;

/**
 * How long silence lasts before the host gives up on a socket.
 *
 * Three missed beats rather than one: a single lost packet on a congested link
 * is ordinary, and closing a live player's connection over it would be the
 * worse trade. Forty-five seconds still beats the three-minute window by a
 * factor of four, which is the whole point of having a heartbeat at all.
 */
export const BATTLE_TRANSPORT_HEARTBEAT_TIMEOUT_MS = BATTLE_TRANSPORT_HEARTBEAT_MS * 3;

/**
 * How many sockets may be open and unauthenticated at once.
 *
 * Measured against a quadrant's capacity rather than a process's memory: the
 * legitimate burst is everybody arriving at once, which is what a full room
 * looks like. Anything beyond it is refused at accept time, before a buffer or
 * a timer exists for it.
 */
export const BATTLE_TRANSPORT_MAX_PENDING = OPEN_WORLD_QUADRANT_CAPACITY;

/**
 * How long before a drain the host warns the clients it holds.
 *
 * It has to cover a round trip to the API for a fresh ticket plus a new socket.
 * Ten seconds is generous for both and is still short enough that an operator
 * draining a host is not waiting on it.
 */
export const BATTLE_TRANSPORT_DRAIN_NOTICE_MS = 10_000;

/**
 * Handshake attempts allowed from one remote address per minute.
 *
 * The room's 90-per-second ceiling is a SESSION limit - it only exists once
 * somebody is seated - so it says nothing about a socket that never gets that
 * far. This is deliberately above the honest worst case: a client whose
 * reconnection retry floor is 500 ms produces about 120 attempts a minute while
 * a host is refusing it, and that client is behaving correctly.
 */
export const BATTLE_TRANSPORT_MAX_HANDSHAKES_PER_MINUTE = 120;

/* -------------------------------------------------------------------------- */
/*  Refusals                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Refusals the client answers by coming back with a fresh ticket.
 *
 * `not_authority` is its own code and not an `unavailable` because the client's
 * response differs: retrying against the same host cannot help, it has to ask
 * the control plane who owns the room.
 */
export const BATTLE_TRANSPORT_RECOVERABLE_REFUSALS = [
  'wrong_epoch',
  'full',
  'draining',
  'not_authority',
  'unavailable',
] as const;

/**
 * Refusals that mean "not with this state".
 *
 * **`unauthorized` deliberately does not split** malformed, forged, expired and
 * already-spent. The ticket verifier already answers all four the same way, and
 * for the same reason: telling them apart tells whoever is trying which of the
 * four they achieved. Learning that a ticket was valid AND already spent
 * confirms it was captured from somewhere real. Inside the host the cases stay
 * distinct; on the wire they do not.
 */
export const BATTLE_TRANSPORT_TERMINAL_REFUSALS = [
  'unauthorized',
  'version',
  'protocol',
  'rate_limit',
  'superseded',
  'destroyed',
] as const;

export const BATTLE_TRANSPORT_REFUSALS = [
  ...BATTLE_TRANSPORT_RECOVERABLE_REFUSALS,
  ...BATTLE_TRANSPORT_TERMINAL_REFUSALS,
] as const;

export type BattleTransportRecoverableRefusal =
  (typeof BATTLE_TRANSPORT_RECOVERABLE_REFUSALS)[number];
export type BattleTransportTerminalRefusal = (typeof BATTLE_TRANSPORT_TERMINAL_REFUSALS)[number];
export type BattleTransportRefusal = (typeof BATTLE_TRANSPORT_REFUSALS)[number];

const RECOVERABLE = new Set<string>(BATTLE_TRANSPORT_RECOVERABLE_REFUSALS);

/** Whether the client may come back with a new ticket, or must stop. */
export function isRecoverableBattleRefusal(
  refusal: BattleTransportRefusal,
): refusal is BattleTransportRecoverableRefusal {
  return RECOVERABLE.has(refusal);
}

/* -------------------------------------------------------------------------- */
/*  Client -> host                                                            */
/* -------------------------------------------------------------------------- */

/**
 * **The first message, and the only one accepted before it.**
 *
 * It carries the ticket and the transport version, and nothing else. Not
 * `accountId`, not the world, not the quadrant, not the epoch, not the profile:
 * every one of those is inside the signed ticket, and a second copy would be a
 * value the host must either ignore - dead weight - or compare, which is a
 * second answer to a question the signature already answered.
 *
 * `tv` travels because it is the one thing the ticket cannot say: it was minted
 * by an API that does not know which transport this client speaks.
 */
export const battleAttachSchema = z
  .object({
    t: z.literal('ta'),
    tv: z.number().int().min(0),
    ticket: z.string().min(OPEN_WORLD_TICKET_MIN_LENGTH).max(OPEN_WORLD_TICKET_MAX_LENGTH),
  })
  .strict();

export type BattleAttach = z.infer<typeof battleAttachSchema>;

/** Proof that a socket is alive. Carries nothing, and advances nothing. */
export const battleHeartbeatSchema = z.object({ t: z.literal('th') }).strict();

export type BattleHeartbeat = z.infer<typeof battleHeartbeatSchema>;

/**
 * Everything a client may say at the TRANSPORT layer, which is two things.
 *
 * Everything else it says is a game frame and is parsed by the room, unchanged,
 * through the same 512-byte ceiling it has always had.
 */
export const battleTransportClientFrameSchema = z.discriminatedUnion('t', [
  battleAttachSchema,
  battleHeartbeatSchema,
]);

export type BattleTransportClientFrame = z.infer<typeof battleTransportClientFrameSchema>;

/* -------------------------------------------------------------------------- */
/*  Host -> client                                                            */
/* -------------------------------------------------------------------------- */

/**
 * **The chair is yours.**
 *
 * Two facts and not a third. `tv` is the transport the host speaks; `gen` is
 * the generation of this chair, minted by the room and until now never told to
 * anybody. The ROOM protocol's version is not repeated here - it already
 * travels in the welcome frame, and a client that cannot speak it disconnects
 * there.
 *
 * `gen` leaves the server because a client can today only discard frames from
 * its own superseded attempt. During a crossing two connections exist on
 * purpose, and with this it can also discard what came from a chair that is no
 * longer its own.
 */
export const battleAcceptedSchema = z
  .object({
    t: z.literal('to'),
    tv: z.literal(BATTLE_TRANSPORT_VERSION),
    gen: z.number().int().min(0),
  })
  .strict();

export type BattleAccepted = z.infer<typeof battleAcceptedSchema>;

/**
 * **This host is going away.**
 *
 * A fact about a machine, not about the game, which is why it lives here and
 * `OPEN_WORLD_CLOSE_REASONS` keeps its six entries. The close that follows uses
 * the `unavailable` the room already has.
 */
export const battleDrainingSchema = z
  .object({
    t: z.literal('td'),
    /** Milliseconds before the socket is closed. Enough to mint and reconnect. */
    inMs: z.number().int().min(0),
  })
  .strict();

export type BattleDraining = z.infer<typeof battleDrainingSchema>;

/** Come back with a new ticket. Said when the host stops being the authority. */
export const battleReconnectSchema = z.object({ t: z.literal('tr') }).strict();

export type BattleReconnect = z.infer<typeof battleReconnectSchema>;

/**
 * **The last thing a refused socket is told.**
 *
 * A code, never a message, never a value, never an echo of what was sent - the
 * rule the upgrade handler and the room's error frame already follow, applied
 * to a new place. The close that follows is code 1000: the reason is a fact
 * about this connection, not about the transport, and a browser does not
 * deliver custom close codes reliably.
 */
export const battleTerminalSchema = z
  .object({
    t: z.literal('tx'),
    c: z.enum(BATTLE_TRANSPORT_REFUSALS),
  })
  .strict();

export type BattleTerminal = z.infer<typeof battleTerminalSchema>;

export const battleTransportServerFrameSchema = z.discriminatedUnion('t', [
  battleAcceptedSchema,
  battleHeartbeatSchema,
  battleDrainingSchema,
  battleReconnectSchema,
  battleTerminalSchema,
]);

export type BattleTransportServerFrame = z.infer<typeof battleTransportServerFrameSchema>;

/**
 * The prefix every transport discriminant carries.
 *
 * Exported so the proof that no game frame collides can read it from here
 * instead of restating it.
 */
export const BATTLE_TRANSPORT_DISCRIMINANT_PREFIX = 't';
